import { useState, type ComponentType } from "react";
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  Eye,
  FileText,
  Lock,
  MapPin,
  User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VBadge,
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VTabs,
  VTabsContent,
  VTabsList,
  VTabsTrigger,
  VTextarea,
} from "@/components/ui";
import type { ReservationRequest } from "@/v2/shared/model";
import type { TaskTarget } from "@/v2/data/db";
import { findAsset } from "@/v2/data/db";
import { fullName } from "@/v2/shared/format";
import { DossierStatusBadge } from "@/v2/shared/statusBadges";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useDossierDetail } from "@/v2/data/queries";
import { useAddDossierComment } from "@/v2/data/mutations";
import { DossierDeployments, DossierOverview } from "@/v2/aanvragen/dossiersTab";
import { DialogFooter } from "@/v2/aanvragen/detailActions";
import { DocumentsTab } from "@/v2/aanvragen/requestDetailPage";
import { CommunicationTab } from "@/v2/aanvragen/communicationTab";
import { TaskGroup } from "@/v2/aanvragen/tasksTab";

// Dossierdetail (CB-13/CB-14): een dossier staat los van de aanvraag en heeft
// een eigen weergave met de toegekende periode, de inzetten en de taken. De
// bovenliggende aanvraag is bereikbaar voor context.
export function DossierDetailPage(): React.JSX.Element {
  const { dossierId = "" } = useParams();
  const navigate = useNavigate();
  const { data, isPending, isError } = useDossierDetail(dossierId);
  const user = useCurrentUser();

  if (isPending) return <LoadingState label="Dossier laden…" />;
  if (isError) return <ErrorMessage title="Kon het dossier niet laden" />;
  if (!data) {
    return (
      <EmptyState
        title="Niet gevonden"
        description="Dit dossier bestaat niet (meer)."
        action={
          <VButton variant="outline" onClick={() => navigate("/dossiers")}>
            Terug naar overzicht
          </VButton>
        }
      />
    );
  }

  const { dossier, request } = data;
  const isManager = user.role === "manager";
  const canManage = isManager && dossier.status !== "CLOSED";
  const canToggle = isManager && dossier.status !== "CLOSED";

  const taskGroups: Array<{ key: string; title: string; target: TaskTarget; tasks: typeof dossier.tasks }> = [
    {
      key: dossier.id,
      title: "Dossier",
      target: { level: "dossier", dossierId: dossier.id },
      tasks: dossier.tasks,
    },
    ...dossier.deployments.map((dep) => {
      const asset = findAsset(dep.assetId);
      return {
        key: dep.id,
        title: `Inzet · ${asset?.number ?? "?"}`,
        target: {
          level: "deployment",
          dossierId: dossier.id,
          deploymentId: dep.id,
        } as TaskTarget,
        tasks: dep.tasks,
      };
    }),
  ];

  return (
    <div className="space-y-6">
      <VButton
        variant="ghost"
        size="sm"
        onClick={() => navigate("/dossiers")}
        className="-ml-3 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Terug naar dossiers
      </VButton>

      <PageHeader
        title={`Dossier ${request.number}`}
        subtitle={`${request.title} · ${request.address}`}
        badge={<DossierStatusBadge status={dossier.status} />}
        actions={
          <VButton
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => navigate(`/aanvragen/${request.id}`)}
          >
            Naar de aanvraag
            <ChevronRight className="size-4" aria-hidden />
          </VButton>
        }
      />

      <VTabs defaultValue="overview">
        <VTabsList>
          <VTabsTrigger value="overview">Overzicht</VTabsTrigger>
          <VTabsTrigger value="deployments" className="gap-2">
            Inzetten
            {dossier.deployments.length > 0 ? (
              <VBadge color="neutral" size="count">
                {dossier.deployments.length}
              </VBadge>
            ) : null}
          </VTabsTrigger>
          <VTabsTrigger value="tasks">Taken</VTabsTrigger>
          <VTabsTrigger value="documents">Documenten</VTabsTrigger>
          <VTabsTrigger value="activity">Communicatie &amp; historiek</VTabsTrigger>
        </VTabsList>

        <VTabsContent value="overview">
          <div className="space-y-4">
            <AanvraagSummary request={request} />
            <DossierOverview request={request} dossier={dossier} canManage={canManage} />
          </div>
        </VTabsContent>

        <VTabsContent value="deployments">
          <DossierDeployments request={request} dossier={dossier} canManage={canManage} />
        </VTabsContent>

        <VTabsContent value="tasks">
          <div className="space-y-3">
            {taskGroups.map((group) => (
              <TaskGroup
                key={group.key}
                requestId={request.id}
                title={group.title}
                target={group.target}
                tasks={group.tasks}
                canToggle={canToggle}
              />
            ))}
          </div>
        </VTabsContent>

        {/* CB-21: documenten hangen centraal aan de aanvraag en zijn ook
            vanuit het dossier raadpleegbaar. */}
        <VTabsContent value="documents">
          <DocumentsTab request={request} />
        </VTabsContent>

        {/* CB-19/CB-20: communicatie en historiek van de aanvraag, ook vanuit
            het dossier raadpleegbaar, aangevuld met de interne dossier-
            opmerkingen. Die zijn enkel zichtbaar voor beheerders (CB-15);
            het dossierdetail is beheerder-only. */}
        <VTabsContent value="activity">
          <div className="space-y-4">
            {canManage ? (
              <div className="flex justify-end">
                <DossierCommentButton requestId={request.id} dossierId={dossier.id} />
              </div>
            ) : null}
            <CommunicationTab request={request} dossier={dossier} />
          </div>
        </VTabsContent>
      </VTabs>
    </div>
  );
}

// CB-15: interne opmerking op het dossier, enkel zichtbaar voor beheerders.
// Bewust zonder mail-vinkje: dossier-opmerkingen bereiken de aanvrager nooit.
function DossierCommentButton({
  requestId,
  dossierId,
}: {
  requestId: string;
  dossierId: string;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const addComment = useAddDossierComment();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  function close(): void {
    setOpen(false);
    setMessage("");
    setError("");
  }

  return (
    <>
      <VButton size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Lock className="size-4" aria-hidden />
        Interne opmerking
      </VButton>
      <VDialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <VDialogContent
          title="Interne opmerking plaatsen"
          description="Deze opmerking hoort bij het dossier en is enkel zichtbaar voor beheerders. De aanvrager ziet ze niet."
        >
          <div className="space-y-4">
            <VField label="Opmerking" htmlFor="dossier-comment" required error={error}>
              <VTextarea
                id="dossier-comment"
                rows={4}
                placeholder="Bv. afspraak met de plaatsingsfirma, interne planning of context bij dit dossier."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setError("");
                }}
              />
            </VField>
            <DialogFooter
              confirmLabel="Plaatsen"
              pending={addComment.isPending}
              onCancel={close}
              onConfirm={() => {
                if (!message.trim()) {
                  setError("Schrijf een opmerking.");
                  return;
                }
                addComment.mutate(
                  { requestId, dossierId, text: message, author },
                  { onSuccess: close },
                );
              }}
            />
          </div>
        </VDialogContent>
      </VDialog>
    </>
  );
}

// Compacte kern van de bovenliggende aanvraag, zodat de behandelaar de context
// (wie, waar, waarom) ziet zonder naar de aanvraag te moeten navigeren.
function AanvraagSummary({ request }: { request: ReservationRequest }): React.JSX.Element {
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="border-b border-border px-5 py-3.5">
        <CardTitle className="text-sm">Aanvraaggegevens</CardTitle>
      </div>
      <dl className="grid gap-x-8 gap-y-4 px-5 py-4 sm:grid-cols-2">
        <SummaryField icon={User} label="Aanvrager">
          {fullName(request.requesterFirstName, request.requesterLastName)}
        </SummaryField>
        <SummaryField icon={Building2} label="Dienst">
          {request.requesterService}
        </SummaryField>
        <SummaryField icon={MapPin} label="Locatie">
          {request.address}
          {request.stadsdeel ? ` · ${request.stadsdeel}` : ""}
        </SummaryField>
        <SummaryField icon={Eye} label="Kijkrichting">
          {request.locationDescription}
        </SummaryField>
        <SummaryField icon={FileText} label="Reden">
          {request.reason}
        </SummaryField>
      </dl>
    </Card>
  );
}

function SummaryField({
  icon: Icon,
  label,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0">
        <dt>
          <Caption tone="muted">{label}</Caption>
        </dt>
        <dd>
          <Body>{children || "-"}</Body>
        </dd>
      </div>
    </div>
  );
}
